import { useEffect, useState } from 'react'

import {
  useW2ReviewSyncRunApi,
  useW2ReviewSyncRunsApi,
  useW2ReviewSyncSettingsApi,
  useW2ReviewSyncSettingsUpdateApi
} from '@/common/api/modules/w2ReviewSync'
import { W2ReviewSyncRun } from '@/common/api/params/w2ReviewSync'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const STATUS_OPTIONS = [
  { value: 'published', label: '公開（W2で公開済みのものをそのまま出す）' },
  { value: 'pending', label: '未確認（自社でもう一度確認してから公開する）' },
  { value: 'hidden', label: '非公開' },
  { value: 'later', label: '保留' }
]

const th =
  'border-primary border px-3 py-2 text-left align-middle text-xs font-normal whitespace-nowrap'
const td = 'border-primary border px-3 py-2 align-middle'

const Kpi = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className='border-primary flex-1 rounded-xl border px-4 py-3'>
    <div className='text-tertiary text-[11px]'>{label}</div>
    <div className='text-emphasis mt-1 text-[22px] font-bold tabular-nums'>{value}</div>
    {sub && <div className='text-tertiary text-[11px]'>{sub}</div>}
  </div>
)

const SEVERITY_STYLE: Record<string, string> = {
  error: 'bg-surface-critical text-critical',
  warning: 'bg-surface-warning text-warning'
}

/** W2レビュー連携（データ基盤 ＞ W2レビュー連携）。
 *
 * W2Commerce から連携で届いたレビューのうち、**承認済みだけ**を UGC Intelligence の
 * レビューへ反映する。反映は連携ファイルの取り込み直後に自動で走る。
 */
const W2ReviewSyncPage = () => {
  const siteId = useSiteId()
  const { data, isLoading, mutate } = useW2ReviewSyncSettingsApi({ site_id: siteId })
  const { data: runs, mutate: mutateRuns } = useW2ReviewSyncRunsApi({
    site_id: siteId,
    limit: 20
  })

  const [enabled, setEnabled] = useState(false)
  const [status, setStatus] = useState('published')
  const [result, setResult] = useState<W2ReviewSyncRun | null>(null)

  useEffect(() => {
    if (!data) return
    setEnabled(data.enabled)
    setStatus(data.default_status)
  }, [data])

  const { trigger: save, isMutating: saving } = useW2ReviewSyncSettingsUpdateApi(() => mutate())
  const { trigger: run, isMutating: running } = useW2ReviewSyncRunApi((r) => {
    setResult(r)
    mutate()
    mutateRuns()
  })

  if (isLoading || !data) {
    return (
      <Main type={MainLayoutType.FULL}>
        <Content>
          <PageListSearching />
        </Content>
      </Main>
    )
  }

  // 承認済みなのに UGC になっていない数。0 でないなら次の反映を待っている状態
  const waiting = Math.max(0, data.approved_total - data.promoted_total)

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='W2レビュー連携'
        description='W2Commerce から連携で届いた商品レビューのうち、管理者チェック済み・公開・未削除のものだけを UGC Intelligence のレビューへ反映します。'
      />

      <Content>
        <Area>
          <Text size={12} color={TextColor.SECONDARY}>
            反映は<strong>連携ファイルの取り込み直後</strong>
            に自動で走ります（W2のデータは8時間おきに届きます）。
            W2側で非公開・削除になったレビューは非表示になり、再公開されれば戻ります。行は消しません。
            モールや他社システムからの移行は、UGC/レビュー ＞
            投稿レビュー管理の「レビューを取り込む」から行います。
          </Text>

          <div className='mt-3 flex flex-wrap gap-3'>
            <Kpi label='データ基盤のW2レビュー' value={data.master_total.toLocaleString()} />
            <Kpi
              label='うち承認済み'
              value={data.approved_total.toLocaleString()}
              sub='チェック済み＋公開＋未削除'
            />
            <Kpi
              label='UGCになっている数'
              value={data.promoted_total.toLocaleString()}
              sub={waiting > 0 ? `未反映 ${waiting.toLocaleString()}件` : '差分なし'}
            />
          </div>

          <div className='mt-5'>
            <Text size={13} weight='bold'>
              連携の設定
            </Text>
            <div className='mt-2 flex flex-wrap items-center gap-3'>
              {/* 左が ON 側（onChange(true)）。他画面も「ON / OFF」の並びで揃えている */}
              <CheckBox
                type={CheckBoxType.TOGGLE_TEXT}
                name='w2_review_import_enabled'
                checked={enabled}
                disabled={saving}
                toggleText={{ left: '有効', right: '無効' }}
                onChange={(v) => {
                  setEnabled(v)
                  save({ site_id: siteId, enabled: v })
                }}
              />
              <Text size={12} color={TextColor.SECONDARY}>
                UGC化したときの公開状態
              </Text>
              <select
                className='border-border-input rounded-md border px-3 py-1.5 text-sm'
                value={status}
                disabled={!enabled || saving}
                onChange={(e) => {
                  setStatus(e.target.value)
                  save({ site_id: siteId, default_status: e.target.value })
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Button
                color={ButtonColor.SECONDARY}
                disabled={!enabled || running}
                loading={running}
                onClick={() => run({ site_id: siteId })}
              >
                今すぐ反映する
              </Button>
            </div>
          </div>

          {result && (
            <div className='border-primary mt-4 rounded-lg border px-3 py-2'>
              <Text size={13} weight='bold'>
                反映結果： {result.status_label}
              </Text>
              <div className='mt-1'>
                <Text size={12} color={TextColor.SECONDARY}>
                  対象 {result.target_count}件 ／ 追加 {result.created_count}件 ／ 更新{' '}
                  {result.updated_count}件 ／ 失敗 {result.failed_count}件 ／ 取り下げで非表示{' '}
                  {result.withdrawn_count}件
                </Text>
              </div>
              {result.rows.length > 0 && (
                <ul className='mt-1 list-disc pl-5'>
                  {result.rows.map((r, i) => (
                    <li key={`${r.review_key}-${i}`}>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {r.review_key} … {r.reason}
                      </Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Area>

        <Area>
          <Text size={13} weight='bold'>
            反映の履歴
          </Text>
          <div className='mt-2 overflow-x-auto'>
            <table className='w-full border-collapse text-sm'>
              <thead>
                <tr className='bg-fill-surface-tertiary'>
                  <th className={th}>日時</th>
                  <th className={th}>契機</th>
                  <th className={th}>結果</th>
                  <th className={th}>対象</th>
                  <th className={th}>追加</th>
                  <th className={th}>更新</th>
                  <th className={th}>失敗</th>
                  <th className={th}>取り下げ</th>
                  <th className={th}>実行者</th>
                </tr>
              </thead>
              <tbody>
                {(runs?.items ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className={`${td} font-mono text-[11px] whitespace-nowrap`}>
                      {r.finished_at ? dateTimeStr(r.finished_at, { omitSecond: true }) : '—'}
                    </td>
                    <td className={`${td} whitespace-nowrap`}>{r.trigger_label}</td>
                    <td className={`${td} whitespace-nowrap`}>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
                          r.failed_count > 0 ? SEVERITY_STYLE.warning : ''
                        }`}
                      >
                        {r.status_label}
                      </span>
                    </td>
                    <td className={`${td} tabular-nums`}>{r.target_count}</td>
                    <td className={`${td} tabular-nums`}>{r.created_count}</td>
                    <td className={`${td} tabular-nums`}>{r.updated_count}</td>
                    <td className={`${td} tabular-nums`}>{r.failed_count}</td>
                    <td className={`${td} tabular-nums`}>{r.withdrawn_count}</td>
                    <td className={td}>{r.triggered_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(runs?.items ?? []).length === 0 && (
              <div className='py-10 text-center'>
                <Text color={TextColor.SECONDARY}>
                  まだ反映していません。連携ファイルが届くと自動で実行されます。
                </Text>
              </div>
            )}
          </div>
        </Area>
      </Content>
    </Main>
  )
}

export default W2ReviewSyncPage
